
import { useState, useEffect } from "react"
import axios from "axios";
import { Modal, Button ,Spinner} from "react-bootstrap"
import Select from "react-select"
import { useSelector, useDispatch } from "react-redux"
import swal from "sweetalert"
import {
  getPatientMenopauseChartAction,
  createPatientMenopauseChartAction,
  updatePatientMenopauseChartAction,
  deletePatientMenopauseChartAction,
} from "../../../../../store/actions/PatientMenopauseAction"
import { sendGenderFormLinkAction } from "../../../../../store/actions/PatientLinkAction"
import { handleSuccess, showErrorAlert } from "../../../../../services/ErrorHandlingService"
import { formatDate } from "../../../../../utils/dateUtils";
import LMPDetails from "./LMPDetails";

const MenopauseChart = () => {
  const dispatch = useDispatch()
  const prescriptionPatientDetails = useSelector(
    (state) => state.prescriptionState?.patientDetails
  );
  const patientDetails = useSelector((state) => state.patients?.patientDetails)
  // const patient_id = patientDetails?.patient_id
  const patient_id = prescriptionPatientDetails?.patient_id || patientDetails?.patient_id;
  const patientEmail =
    prescriptionPatientDetails?.patient_email || patientDetails?.patient_email;

  // Redux state with fallback values
  const patientMenopauseState = useSelector((state) => state.patientMenopause || {})
  const { patientMenopauseCharts = [], isLoading = false, error = null } = patientMenopauseState
  const [submitting, setSubmitting] = useState(false);

  const [showModal, setShowModal] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [currentEditId, setCurrentEditId] = useState(null)
  const [sending, setSending] = useState(false)
  const [showLMPModal, setShowLMPModal] = useState(false);
  const [lmpList, setLmpList] = useState([]);
  const [editingLMPIndex, setEditingLMPIndex] = useState(null);
  const [editingLMPData, setEditingLMPData] = useState(null);

  const [formData, setFormData] = useState({
    menopause_date: "",
    age_first_m: "",
    menstruation: "",
    how_long_m_stopped: "",
    how_long_m_stopped_unit: "Month",
    causes_m: "",
    other_name: "",
    how_many_days_occur: "",
  })
  // const handleSendLink = async () => {
  //   if (!patientEmail) {
  //     swal("Error", "No email found for this patient", "error");
  //     return;
  //   }

  //   try {
  //     const response = await axios.post("/api/send-menopause-link", {
  //       email: patientEmail,
  //       patientId: patient_id,
  //       link: `https://yourfrontend.com/patient/${patient_id}/menopause-form`
  //     });

  //     if (response.data.success) {
  //       swal("Success", "Link sent successfully to patient's email!", "success");
  //     } else {
  //       swal("Error", response.data.message || "Failed to send link", "error");
  //     }
  //   } catch (error) {
  //     console.error(error);
  //     swal("Error", "An error occurred while sending the link", "error");
  //   }
  // };

  // Fetch menopause chart data on component mount
  useEffect(() => {
    if (patient_id) {
      dispatch(getPatientMenopauseChartAction(patient_id))
    }
  }, [patient_id, dispatch])

  const handleModalClose = () => {
    setShowModal(false)
    resetForm()
  }

  const handleAddClick = () => {
    setIsEditMode(false)
    setCurrentEditId(null)
    resetForm()
    setShowModal(true)
  }

  const handleEditClick = (item) => {
    setIsEditMode(true)
    setCurrentEditId(item.menopause_id)

    // Format the date for the input field
    const dateObj = new Date(item.menopause_date)
    const formattedDate = dateObj.toISOString().split("T")[0]

    // Parse the duration field if it exists
    const durationParts = item.how_long_m_stopped ? item.how_long_m_stopped.split(" ") : ["", "Month"]
    const duration = durationParts[0] || ""
    const unit = durationParts[1] || "Month"

    // Populate the form with the existing data
    setFormData({
      menopause_date: formattedDate,
      age_first_m: item.age_first_m,
      menstruation: item.menstruation,
      how_long_m_stopped: duration,
      how_long_m_stopped_unit: unit,
      causes_m: item.causes_m || "",
      other_name: item.other_name || "",
      how_many_days_occur: item.how_many_days_occur,
    })

    setShowModal(true)
  }

  const handleDeleteClick = async (item) => {
    try {
      const willDelete = await swal({
        title: "Are you sure?",
        text: "Once deleted, you will not be able to recover this record!",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      })

      if (willDelete) {
        console.log("Deleting item:", item) // Debug log

        // Pass both the item and patient_id to the delete action
        const result = await dispatch(deletePatientMenopauseChartAction(item, patient_id))

        console.log("Delete result:", result) // Debug log

        // Force refresh the data after deletion
        if (result && result.success) {
          // Wait a moment then refresh to ensure backend is updated
          setTimeout(() => {
            dispatch(getPatientMenopauseChartAction(patient_id))
          }, 500)
        }
      }
    } catch (error) {
      console.error("Error deleting record:", error)
      swal("Error", "An error occurred while deleting the record", "error")
    }
  }

  const resetForm = () => {
    setFormData({
      menopause_date: "",
      age_first_m: "",
      menstruation: "",
      how_long_m_stopped: "",
      how_long_m_stopped_unit: "Month",
      causes_m: "",
      other_name: "",
      how_many_days_occur: "",
    })
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : "",
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate that patient_id exists
    if (!patient_id) {
      showErrorAlert("Patient ID is missing. Please refresh the page and try again.", "error")
      return
    }
    setSubmitting(true);
    try {
      // Combine duration and unit for submission
      const submissionData = {
        ...formData,
        how_long_m_stopped:
          formData.how_long_m_stopped && formData.how_long_m_stopped_unit
            ? `${formData.how_long_m_stopped} ${formData.how_long_m_stopped_unit}`
            : "",
      }

      // Remove the separate unit field before submission
      delete submissionData.how_long_m_stopped_unit

      let result

      if (isEditMode && currentEditId) {
        // Update existing record - pass patient_id as third parameter
        result = await dispatch(updatePatientMenopauseChartAction(submissionData, currentEditId, patient_id))
      } else {
        // Create new record - pass patient_id as second parameter
        result = await dispatch(createPatientMenopauseChartAction(submissionData, patient_id))
      }

      if (result && result.success) {
        handleModalClose()
        // Refresh the data after successful operation
        dispatch(getPatientMenopauseChartAction(patient_id))
      }
    } catch (error) {
      console.error("Error saving record:", error)
      showErrorAlert("An error occurred while saving the record", "error")
    }
    finally {
      setSubmitting(false); 
    }
  }
// Edit LMP Entry
const handleOpenAddLMP = () => {
  setEditingLMPIndex(null);
  setEditingLMPData(null);
  setShowLMPModal(true);
};

// Edit LMP entry: set editing states and open modal
const handleEditLMP = (entry, index) => {
  // ensure the date is in yyyy-mm-dd format for the input
  const formattedEntry = {
    ...entry,
    // if entry.date is a Date string, convert to yyyy-mm-dd, else assume already yyyy-mm-dd
    date: entry.date ? new Date(entry.date).toISOString().split("T")[0] : "",
    daysOfFlow: entry.daysOfFlow || "",
    comment: entry.comment || "",
  };
  setEditingLMPIndex(index);
  setEditingLMPData(formattedEntry);
  setShowLMPModal(true);
};

// Save LMP from modal (both add and update)
const handleSaveLMP = (newLmp) => {
  // normalize incoming date to ISO string or store as provided
  const normalized = {
    date: newLmp.date,
    daysOfFlow: newLmp.daysOfFlow,
    comment: newLmp.comment,
  };

  if (editingLMPIndex !== null && editingLMPIndex !== undefined) {
    const updatedList = [...lmpList];
    updatedList[editingLMPIndex] = normalized;
    setLmpList(updatedList);
  } else {
    // add newest on top (same behavior you had before)
    setLmpList([normalized, ...lmpList]);
  }

  // reset editing states
  setEditingLMPIndex(null);
  setEditingLMPData(null);
  setShowLMPModal(false);
};

// Delete LMP entry
const handleDeleteLMP = (index) => {
  const updatedList = lmpList.filter((_, i) => i !== index);
  setLmpList(updatedList);
  // if deleting the one currently in edit mode, clear edit
  if (editingLMPIndex === index) {
    setEditingLMPIndex(null);
    setEditingLMPData(null);
    setShowLMPModal(false);
  }
};

const getRoleFromCreatedBy = (createdBy) => {
  switch (String(createdBy)) {
    case "14":
      return "SERVICE_CALLER";
    case "20":
      return "DOCTOR";
    case "27":
      return "PATIENT";
    default:
      return "PATIENT";
  }
};

const roleStyles = {
  SERVICE_CALLER: {
    className: "bg-info bg-opacity-25 ",
    label: "Service Caller",
  },
  DOCTOR: {
    className: "bg-primary bg-opacity-10 ",
    label: "Doctor",
  },
  PATIENT: {
    className: "",
    label: "Patient",
  },
};



  // Format date for display
  // const formatDate = (dateString) => {
  //   const date = new Date(dateString)
  //   const day = date.getDate()
  //   const month = date.toLocaleString("default", { month: "short" })
  //   const year = date.getFullYear()

  //   return {
  //     label: `${day} ${month}`,
  //     year: year.toString(),
  //   }
  // }

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "50px",
      height: "50px",
    }),
    singleValue: (provided) => ({
      ...provided,
      lineHeight: "50px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: "none",
    }),
  }

  // Define attributes for the table
  const attributes = [
    "Age of first menstruation",
    "Menstruation",
    "How long has menstruation stopped?",
    "What causes menstruation?",
    "How many days does menstruation occur?",
  ]

  // Show loading or error if patient_id is not available
  if (!patient_id) {
    return (
      <div className="row">
        <div className="col-xl-12">
          <div className="card shadow-none">
            <div className="card-body">
              <div className="text-center py-4">
                <div className="alert alert-warning" role="alert">
                  Patient information is not available. Please select a patient first.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="card shadow-none">
          <div className="card-body">
            <div className="text-end mb-3 d-flex gap-2 justify-content-end">
            {/* <Button
    variant="info"
    className="text-white"
    onClick={() => setShowLMPModal(true)}
  >
    <i className="bi bi-droplet me-2"></i> Add LMP
  </Button>
              <button className="btn btn-primary" onClick={handleAddClick}>
                Add
              </button>

              <Button
                variant="primary"
                className="btn btn-primary"
                disabled={sending} // ✅ disable while loading
                onClick={async () => {
                  if (!patient_id) {
                    showErrorAlert("Patient ID not found!");
                    return;
                  }
                  try {
                    setSending(true); // 🔄 start loading
                    await dispatch(sendGenderFormLinkAction(patient_id, "MENOPAUSE"));
                    //handleSuccess("Link sent successfully!");
                  } catch (err) {
                    console.error(err);
                    showErrorAlert("Failed to send link");
                  } finally {
                    setSending(false); // ⏹ stop loading
                  }
                }}
              >
                {sending ? "Sending..." : "Send Link"}
              </Button> */}

<Button
  variant="outline-info"
  size="sm"

  onClick={() => setShowLMPModal(true)}
>
  <i className="bi bi-droplet me-2"></i> Add LMP
</Button>

<Button
  variant="outline-primary"
  size="sm"
  onClick={handleAddClick}
>
  Add
</Button>

<Button
  variant="outline-primary"
  size="sm"
  disabled={sending}
  onClick={async () => {
    if (!patient_id) {
      showErrorAlert("Patient ID not found!");
      return;
    }
    try {
      setSending(true);
      await dispatch(sendGenderFormLinkAction(patient_id, "MENOPAUSE"));
    } catch (err) {
      console.error(err);
      showErrorAlert("Failed to send link");
    } finally {
      setSending(false);
    }
  }}
>
  {sending ? "Sending..." : "Send Link"}
</Button>


              {/* <a
  href={`mailto:${patientEmail}?subject=Menopause Form Link&body=Hello, please fill the form at https://yourfrontend.com/patient/${patient_id}/menopause-form`}
  className="btn btn-success"
>
  Send Link
</a> */}

            </div>
            {/* LMP Report (Menopause-style table layout) */}
<div className="mt-4">
  {/* <h6 className="fw-bold text-info mb-3">LMP Report</h6> */}
  <div className="table-responsive">
    {lmpList && lmpList.length > 0 ? (
      <table
        className="table header-border"
        style={{
          width: "100%",
          borderCollapse: "collapse",
          border: "2px solid #ddd",
        }}
      >
        <thead>
          <tr className="table-info">
            <th scope="col"></th>
            {lmpList.map((entry, index) => (
              <th
                key={index}
                scope="col"
                style={{
                  color: "black",
                  fontWeight: "bold",
                  textAlign: "center",
                }}
              >
                <div>{new Date(entry.date).toLocaleDateString()}</div>
                <div className="mt-2 d-flex justify-content-center gap-3">
                  <i
                    className="fa fa-pencil-alt text-success"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleEditLMP(entry, index)} // ✅ opens LMP modal
                  />
                  <i
                    className="fa fa-trash text-danger"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleDeleteLMP(index)} // ✅ delete LMP entry
                  />
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={{ backgroundColor: "white", textAlign: "center" }}>
          {/* Days of Flow Row */}
          <tr>
            <th style={{ textAlign: "left", fontWeight: "normal" }}>
              No. of Days of Flow
            </th>
            {lmpList.map((entry, index) => (
              <td key={index}>{entry.daysOfFlow || "-"}</td>
            ))}
          </tr>

          {/* Comments Row */}
          <tr>
            <th style={{ textAlign: "left", fontWeight: "normal" }}>
              Comments
            </th>
            {lmpList.map((entry, index) => (
              <td key={index}>{entry.comment || "-"}</td>
            ))}
          </tr>
        </tbody>
      </table>
    ) : (
      <div className="text-center py-4">
        <p>No LMP data available</p>
      </div>
    )}
  </div>
</div>

            <div className="table-responsive">
              {isLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="text-center py-4">
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                </div>
              ) : patientMenopauseCharts && patientMenopauseCharts.length > 0 ? (
                <table
                  className="table header-border"
                  style={{ width: "100%", borderCollapse: "collapse", border: "2px solid #ddd" }}
                >
                  <thead>
                    <tr className="table-info">
                      <th scope="col"></th>
                      {patientMenopauseCharts.map((item) => {
                          const role = getRoleFromCreatedBy(item.created_by);
                          const roleStyle = roleStyles[role];
                        const formattedDate = formatDate(item.menopause_date)
                        return (
                          <th
                            key={item.menopause_id}
                            scope="col"
                            className={roleStyle.className} 
                            style={{ color: "black", fontWeight: "bold", textAlign: "center" }}
                          >
                              <div>{formatDate(item.menopause_date, false, false, true)}</div>
                            <div className="mt-2 d-flex justify-content-center gap-3">
                              <i
                                className="fa fa-pencil-alt text-success"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleEditClick(item)}
                              />
                              <i
                                className="fa fa-trash text-danger"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleDeleteClick(item)}
                              />
                            </div>
                           
                          </th>
                        )
                      })}
                    </tr>
                  </thead>
                  <tbody style={{ backgroundColor: "white", textAlign: "center" }}>
                    {attributes.map((attr, index) => (
                      <tr key={index}>
                        <th style={{ textAlign: "left", fontWeight: "normal" }}>{attr}</th>
                        {patientMenopauseCharts.map((item) => {
                                   const role = getRoleFromCreatedBy(item.created_by);
                                   const roleStyle = roleStyles[role];
                            
                          let value = ""
                          switch (index) {
                            case 0:
                              value = item.age_first_m
                              break
                            case 1:
                              value = item.menstruation
                              break
                            case 2:
                              value = item.how_long_m_stopped
                              break
                            case 3:
                              value = item.causes_m
                              break
                            case 4:
                              value = item.how_many_days_occur
                              break
                          }
                          return <td key={item.menopause_id}    className={roleStyle.className}   // ✅ APPLY STYLE HERE
                          >{value}</td>
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-4">
                  <p>No menopause chart data available</p>
                </div>
              )}
            </div>
{/* LMP Modal Component */}
    {/* LMP Modal Component */}
    <LMPDetails
              show={showLMPModal}
              onHide={() => {
                setShowLMPModal(false);
                setEditingLMPIndex(null);
                setEditingLMPData(null);
              }}
              onSave={(newLmp) => handleSaveLMP(newLmp)}
              editData={editingLMPData}
            />





            <Modal show={showModal} onHide={handleModalClose} size="lg" centered>
              <Modal.Header closeButton>
                <Modal.Title>{isEditMode ? "Edit Menopause Data" : "Add Menopause Data"}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Date<span style={{ color: "red" }}>*</span></label>
                    <div className="position-relative">
                      <input
                        type="date"
                        className="form-control pe-5"
                        name="menopause_date"
                        value={formData.menopause_date}
                        onChange={handleInputChange}
                        max={new Date().toISOString().split("T")[0]}
                        required
                      />
                      <i
                        className="fa fa-calendar position-absolute"
                        style={{
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                          fontSize: "18px",
                          color: "#6c757d",
                        }}
                      ></i>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Age of First Menstruation<span style={{ color: "red" }}>*</span></label>
                    <Select
                      name="age_first_m"
                      options={[...Array(91).keys()].map((age) => ({
                        value: String(age + 10),
                        label: String(age + 10),
                      }))}
                      value={
                        formData.age_first_m
                          ? {
                            value: formData.age_first_m,
                            label: formData.age_first_m,
                          }
                          : null
                      }
                      onChange={(option, action) => handleSelectChange(option, action)}
                      placeholder="Select Age"
                      styles={customSelectStyles}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Menstruation<span style={{ color: "red" }}>*</span></label>
                    <Select
                      name="menstruation"
                      options={[
                        { value: "Regular", label: "Regular" },
                        { value: "Irregular", label: "Irregular" },
                        { value: "Stopped", label: "Stopped" },
                      ]}
                      value={
                        formData.menstruation ? { value: formData.menstruation, label: formData.menstruation } : null
                      }
                      onChange={(option, action) => handleSelectChange(option, action)}
                      placeholder="Select Status"
                      styles={customSelectStyles}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">How long has menstruation stopped?</label>
                    <div className="row">
                      <div className="col-8">
                        <input
                          type="number"
                          className="form-control"
                          name="how_long_m_stopped"
                          placeholder="Enter duration"
                          value={formData.how_long_m_stopped}
                          onChange={handleInputChange}
                          min="0"
                        />
                      </div>
                      <div className="col-4">
                        <Select
                          name="how_long_m_stopped_unit"
                          options={[
                            { value: "Month", label: "Month" },
                            { value: "Year", label: "Year" },
                          ]}
                          value={
                            formData.how_long_m_stopped_unit
                              ? {
                                value: formData.how_long_m_stopped_unit,
                                label: formData.how_long_m_stopped_unit,
                              }
                              : null
                          }
                          onChange={(option, action) => handleSelectChange(option, action)}
                          placeholder="Unit"
                          styles={customSelectStyles}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">What causes menstruation?<span style={{ color: "red" }}>*</span></label>
                    <Select
                      name="causes_m"
                      options={[
                        { value: "Occurs naturally", label: "Occurs naturally" },
                        {
                          value: "Occurs only with medication (tablets)",
                          label: "Occurs only with medication (tablets)",
                        },
                        {
                          value: "Sometimes naturally, sometimes with medication",
                          label: "Sometimes naturally, sometimes with medication",
                        },
                        { value: "Other", label: "Other" },
                      ]}
                      value={formData.causes_m ? { value: formData.causes_m, label: formData.causes_m } : null}
                      onChange={(option, action) => handleSelectChange(option, action)}
                      placeholder="Select Cause"
                      styles={customSelectStyles}
                      required
                    />
                  </div>

                  {formData.causes_m === "Other" && (
                    <div className="mb-3">
                      <label className="form-label">Other Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="other_name"
                        placeholder="Please specify other cause"
                        value={formData.other_name}
                        onChange={handleInputChange}
                      />
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">How many days does menstruation occur?<span style={{ color: "red" }}>*</span></label>
                    <Select
                      name="how_many_days_occur"
                      options={[
                        { value: "10–15 days", label: "10–15 days" },
                        { value: "16–23 days", label: "16–23 days" },
                        { value: "24–28 days", label: "24–28 days" },
                        { value: "28–30 days", label: "28–30 days" },
                        { value: "31–45 days", label: "31–45 days" },
                        { value: "Every 45 days", label: "Every 45 days" },
                        { value: "Every 2 months", label: "Every 2 months" },
                        { value: "More than 2 months", label: "More than 2 months" },
                        { value: "Cannot say", label: "Cannot say" },
                        { value: "Sometimes early, sometimes late", label: "Sometimes early, sometimes late" },
                      ]}
                      value={
                        formData.how_many_days_occur
                          ? {
                            value: formData.how_many_days_occur,
                            label: formData.how_many_days_occur,
                          }
                          : null
                      }
                      onChange={(option, action) => handleSelectChange(option, action)}
                      placeholder="Select Duration"
                      styles={customSelectStyles}
                      required
                    />
                  </div>

                  <div className="text-end">
                    {/* <Button type="submit" variant="primary" disabled={isLoading}>
                      {isLoading ? "Saving..." : isEditMode ? "Update" : "Save"}
                    </Button> */}
                    <Button type="submit" variant="primary" disabled={submitting}>
  {submitting ? (
    <>
      <Spinner animation="border" size="sm" className="me-2" />
      {isEditMode ? "Updating..." : "Saving..."}
    </>
  ) : (
    isEditMode ? "Update" : "Save"
  )}
</Button>

                  </div>
                </form>
              </Modal.Body>
            </Modal>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MenopauseChart
